/**
 * File Manager Page - The Code Deck
 * ==================================
 * Browse files and edit code in the browser!
 * Like a mini VS Code inside your browser.
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { 
  Container, 
  ChevronLeft,
  Loader2,
  Terminal,
  Activity,
  Brain,
  LogOut,
  Flame,
  Folder,
  FileText,
  FileCode,
  Save,
  X,
  ChevronRight,
  FolderOpen,
  File,
  FileJson,
  FileType,
  RefreshCw,
  Trophy
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ activePage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { id: "dashboard", label: "MISSION CONTROL", icon: <Activity className="w-5 h-5" />, path: "/dashboard" },
    { id: "sites", label: "DEPLOYMENTS", icon: <Container className="w-5 h-5" />, path: "/sites" },
    { id: "ml-studio", label: "ROBOT GYM", icon: <Brain className="w-5 h-5" />, path: "/ml-studio" },
    { id: "tutorials", label: "GUIDES", icon: <Trophy className="w-5 h-5" />, path: "/tutorials" },
    { id: "family", label: "FAMILY", icon: <Flame className="w-5 h-5" />, path: "/family" },
  ];

  return (
    <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Terminal className="w-6 h-6 text-black" />
          </div>
          <span className="text-xl font-black text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CODEFORGE
          </span>
        </div>
      </div>

      <div className="p-4 m-4 bg-black/30 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono text-muted-foreground uppercase">LEVEL {user?.level || 1}</span>
          <span className="text-xs font-mono text-primary">{user?.xp || 0} XP</span>
        </div>
        <Progress value={(user?.xp || 0) % 100} className="h-2 bg-muted" />
        <div className="flex items-center gap-2 mt-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-xs font-mono text-white">{user?.streak || 0} day streak</span>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-mono transition-colors ${
                activePage === item.id
                  ? "bg-primary/10 text-primary border-l-2 border-primary"
                  : "text-muted-foreground hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-primary font-bold text-sm">{user?.username?.[0]?.toUpperCase()}</span>
            </div>
            <span className="text-sm font-mono text-white">{user?.username}</span>
          </div>
          <button onClick={logout} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Get icon for file type
const getFileIcon = (name, type) => {
  if (type === "folder") return <Folder className="w-4 h-4 text-blue-400" />;
  
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'php':
      return <FileCode className="w-4 h-4 text-purple-400" />;
    case 'js':
    case 'jsx':
      return <FileCode className="w-4 h-4 text-yellow-400" />;
    case 'json':
      return <FileJson className="w-4 h-4 text-green-400" />;
    case 'md':
      return <FileType className="w-4 h-4 text-blue-400" />;
    case 'env':
      return <FileText className="w-4 h-4 text-orange-400" />;
    case 'py':
      return <FileCode className="w-4 h-4 text-blue-400" />;
    case 'c':
    case 'h':
      return <FileCode className="w-4 h-4 text-cyan-400" />;
    default:
      return <File className="w-4 h-4 text-muted-foreground" />;
  }
};

// Get syntax highlighting class
const getSyntaxClass = (language) => {
  switch (language) {
    case 'php':
      return 'text-purple-300';
    case 'json':
      return 'text-green-300';
    case 'markdown':
      return 'text-blue-300';
    default:
      return 'text-white';
  }
};

const FileManagerPage = () => {
  const navigate = useNavigate();
  const { siteId } = useParams();
  const { refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("/");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['/']));

  useEffect(() => {
    fetchData();
  }, [siteId]);

  const fetchData = async () => {
    try {
      const [siteRes, filesRes] = await Promise.all([
        api.get(`/sites/${siteId}`),
        api.get(`/sites/${siteId}/files?path=/`),
      ]);
      setSite(siteRes.data);
      setFiles(filesRes.data.files);
    } catch (error) {
      toast.error("Failed to load file manager");
      navigate("/sites");
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = async (file) => {
    if (file.type === "folder") {
      // Toggle folder expansion
      const newExpanded = new Set(expandedFolders);
      if (newExpanded.has(file.path)) {
        newExpanded.delete(file.path);
      } else {
        newExpanded.add(file.path);
      }
      setExpandedFolders(newExpanded);
    } else {
      // Load file content
      try {
        const res = await api.get(`/sites/${siteId}/files/content?path=${file.path}`);
        setSelectedFile({ ...file, language: res.data.language });
        setFileContent(res.data.content);
        setOriginalContent(res.data.content);
      } catch (error) {
        toast.error("Failed to load file");
      }
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    
    setSaving(true);
    try {
      const res = await api.post(`/sites/${siteId}/files/save`, {
        path: selectedFile.path,
        content: fileContent,
        language: selectedFile.language,
      });
      setOriginalContent(fileContent);
      toast.success("File saved!", { description: `+${res.data.xp_earned} XP earned!` });
      await refreshUser();
    } catch (error) {
      toast.error("Failed to save file");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = fileContent !== originalContent;

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#0B0D10]">
        <Sidebar activePage="sites" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground font-mono">Loading Code Deck...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#0B0D10]">
      <Sidebar activePage="sites" />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#16191F]/50 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/sites/${siteId}`)}
                className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors font-mono text-sm"
                data-testid="back-to-site-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                BACK
              </button>
              <div className="h-6 w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-primary" />
                <span className="font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  CODE DECK
                </span>
                <span className="text-xs text-muted-foreground font-mono">
                  - {site?.name}
                </span>
              </div>
            </div>
            
            {selectedFile && (
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <span className="text-xs text-yellow-400 font-mono">Unsaved changes</span>
                )}
                <Button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="bg-green-500 text-black font-bold hover:bg-green-400 disabled:opacity-50"
                  data-testid="save-file-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  SAVE
                </Button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* File Tree */}
          <div className="w-64 bg-[#16191F] border-r border-white/5 flex flex-col">
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-xs font-mono text-muted-foreground uppercase">FILES</span>
              <button
                onClick={fetchData}
                className="text-muted-foreground hover:text-white"
                data-testid="refresh-files-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2">
                {files.map((file) => (
                  <button
                    key={file.path}
                    onClick={() => handleFileClick(file)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-mono transition-colors hover:bg-white/5 ${
                      selectedFile?.path === file.path
                        ? "bg-primary/10 text-primary border-l-2 border-primary"
                        : "text-muted-foreground hover:text-white"
                    }`}
                    data-testid={`file-${file.name}`}
                  >
                    {file.type === "folder" ? (
                      expandedFolders.has(file.path) ? (
                        <FolderOpen className="w-4 h-4 text-blue-400" />
                      ) : (
                        <Folder className="w-4 h-4 text-blue-400" />
                      )
                    ) : (
                      getFileIcon(file.name, file.type)
                    )}
                    <span className="truncate">{file.name}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Editor */}
          <div className="flex-1 flex flex-col bg-[#0D1117]">
            {selectedFile ? (
              <>
                {/* Tab Bar */}
                <div className="border-b border-white/5 flex items-center">
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#16191F] border-r border-white/5">
                    {getFileIcon(selectedFile.name, 'file')}
                    <span className="text-sm font-mono text-white">{selectedFile.name}</span>
                    {hasChanges && (
                      <span className="w-2 h-2 bg-yellow-400 rounded-full" />
                    )}
                    <button
                      onClick={() => {
                        setSelectedFile(null);
                        setFileContent("");
                        setOriginalContent("");
                      }}
                      className="ml-2 text-muted-foreground hover:text-white"
                      data-testid="close-file-btn"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Editor Content */}
                <div className="flex-1 p-4 overflow-auto">
                  <Textarea
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    className={`w-full h-full min-h-[500px] bg-transparent border-none resize-none font-mono text-sm leading-6 focus:ring-0 focus:outline-none ${getSyntaxClass(selectedFile.language)}`}
                    placeholder="// Start coding here..."
                    spellCheck={false}
                    data-testid="code-editor"
                  />
                </div>

                {/* Status Bar */}
                <div className="border-t border-white/5 px-4 py-2 flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <span>{selectedFile.language.toUpperCase()}</span>
                    <span>|</span>
                    <span>{fileContent.split('\n').length} lines</span>
                    <span>|</span>
                    <span>{fileContent.length} chars</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${hasChanges ? 'bg-yellow-400' : 'bg-green-400'}`} />
                    <span>{hasChanges ? 'Modified' : 'Saved'}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <FileCode className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    NO FILE SELECTED
                  </h3>
                  <p className="text-muted-foreground font-mono text-sm">
                    Select a file from the tree to start editing
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileManagerPage;
